import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Search } from '../../icons';
import { tCalendar } from '../../lib/calendar-i18n';
import {
  CONTACT_PAGE, filterContactLines, loadAttendeeContacts, mergeContactSections,
  type AttendeeContactsData, type ContactLine,
} from './lib/externalAttendees';
import './AttendeeContacts.css';

/**
 * 참석자 고르기의 「주소록」 탭 (2026-11-02 · 지시 57).
 *
 * - **처음 그려질 때 한 번만** 부른다 — 탭을 열기 전에는 이 부품이 없다(`AttendeePickerPanel` 이 처음 열 때 붙인다).
 *   받은 것은 세션 동안 `loadAttendeeContacts` 가 기억한다(요청 한도).
 * - 「내 주소록」·「공유 주소록」 두 묶음. 같은 이메일은 하나로 합친다(`mergeContactSections`).
 * - 체크로 고르고, 목록 «안»에서 찾는다(서버 검색을 안 부른다).
 * - `lockedNote` 가 있으면 체크를 잠그고 그 까닭을 한 줄 둔다.
 */
interface Props {
  picked: ReadonlySet<string>;
  onToggle: (line: ContactLine) => void;
  lockedNote?: string | null;
}

type State =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: AttendeeContactsData };

export function AttendeeContactsTab({ picked, onToggle, lockedNote = null }: Props) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [query, setQuery] = useState('');

  const load = useCallback(() => {
    let alive = true;
    setState({ kind: 'loading' });
    loadAttendeeContacts()
      .then((data) => { if (alive) setState({ kind: 'ready', data }); })
      .catch(() => { if (alive) setState({ kind: 'error' }); });
    return () => { alive = false; };
  }, []);

  useEffect(() => load(), [load]);

  const sections = useMemo(
    () => (state.kind === 'ready' ? mergeContactSections(state.data.personal, state.data.shared) : null),
    [state],
  );
  const shown = useMemo(
    () => (sections ? { personal: filterContactLines(sections.personal, query), shared: filterContactLines(sections.shared, query) } : null),
    [sections, query],
  );

  const renderLine = (line: ContactLine) => {
    const on = picked.has(line.contactId);
    const org = [line.company, line.department].filter(Boolean).join(' · ');
    return (
      <li key={line.contactId}>
        <label className="calendar-attcontacts-row">
          <input
            type="checkbox"
            checked={on}
            disabled={Boolean(lockedNote)}
            onChange={() => onToggle(line)}
          />
          <span className="calendar-attcontacts-text">
            <span className="calendar-attcontacts-name">{line.name ?? line.email}</span>
            {org && <span className="calendar-attcontacts-sub">{org}</span>}
            <span className="calendar-attcontacts-sub">{line.email}</span>
            {line.alsoShared && <span className="calendar-attcontacts-sub">{tCalendar('calendar.attendee_panel.contacts_also_shared')}</span>}
          </span>
        </label>
      </li>
    );
  };

  const section = (title: string, lines: ContactLine[], testId: string) => (
    <section className="calendar-attcontacts-section" data-testid={testId}>
      <h4 className="calendar-attcontacts-head">{title}</h4>
      {lines.length > 0 ? (
        <ul className="calendar-attpanel-list">{lines.map(renderLine)}</ul>
      ) : (
        <p className="calendar-attpanel-msg">{tCalendar('calendar.attendee_panel.contacts_empty')}</p>
      )}
    </section>
  );

  return (
    <div className="calendar-attcontacts" data-testid="attendee-contacts-tab">
      <label className="calendar-attpanel-search">
        <Search size={14} aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={tCalendar('calendar.attendee_panel.contacts_search')}
          aria-label={tCalendar('calendar.attendee_panel.contacts_search')}
        />
      </label>
      <p className="calendar-attcontacts-note" role={lockedNote ? 'note' : undefined}>
        {lockedNote ?? tCalendar('calendar.attendee_panel.contacts_hint')}
      </p>
      <div className="calendar-attpanel-tree">
        {state.kind === 'loading' && (
          <p className="calendar-attpanel-msg"><Loader2 size={13} className="calendar-spin" aria-hidden /> {tCalendar('calendar.attendee_panel.contacts_loading')}</p>
        )}
        {state.kind === 'error' && (
          <p className="calendar-attpanel-msg" role="alert">
            {tCalendar('calendar.attendee_panel.contacts_failed')}{' '}
            <button type="button" className="calendar-attcontacts-retry" onClick={() => load()}>
              {tCalendar('calendar.attendee_panel.contacts_retry')}
            </button>
          </p>
        )}
        {state.kind === 'ready' && shown && (
          <>
            {section(tCalendar('calendar.attendee_panel.contacts_mine', { n: state.data.personalTotal }), shown.personal, 'attendee-contacts-mine')}
            {section(tCalendar('calendar.attendee_panel.contacts_shared', { n: state.data.sharedTotal }), shown.shared, 'attendee-contacts-shared')}
            {(state.data.personalTotal > CONTACT_PAGE || state.data.sharedTotal > CONTACT_PAGE) && (
              <p className="calendar-attpanel-msg">{tCalendar('calendar.attendee_panel.contacts_more', { n: CONTACT_PAGE })}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
